import React, { forwardRef, useMemo } from "react";
import Button from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import { Dropdown } from "primereact/dropdown";
import styles from "./Paginator.module.css";

export interface PaginatorPageChangeEvent {
  first: number;
  rows: number;
  page: number; // 0-based index
  pageCount: number;
}

export interface PaginatorProps {
  totalRecords: number;
  rows: number;
  first?: number;
  rowsPerPageOptions?: number[];
  onPageChange: (event: PaginatorPageChangeEvent) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const Paginator = forwardRef<HTMLDivElement, PaginatorProps>(
  ({
    totalRecords,
    rows,
    first = 0,
    rowsPerPageOptions = [10, 20, 50],
    onPageChange,
    className,
    style,
    ...rest
  }, ref) => {

    const currentPage = Math.floor(first / rows) + 1;
    const totalPages = Math.max(1, Math.ceil(totalRecords / rows));
    const lastRecord = Math.min(first + rows, totalRecords);
    const firstRecord = totalRecords === 0 ? 0 : first + 1;

    const changePage = (newPage: number): void => {
      if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
        onPageChange({
          first: (newPage - 1) * rows,
          rows,
          page: newPage - 1,
          pageCount: totalPages
        });
      }
    };

    const changeRowsPerPage = (newRows: number): void => {
      onPageChange({
        first: 0,
        rows: newRows,
        page: 0,
        pageCount: Math.ceil(totalRecords / newRows)
      });
    };

    const visiblePages = useMemo((): (number | string)[] => {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      if (currentPage <= 4) {
        return [1, 2, 3, 4, 5, '...', totalPages];
      }
      if (currentPage >= totalPages - 3) {
        return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      }
      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    }, [currentPage, totalPages]);

    const rppOptions = rowsPerPageOptions.map(r => ({ label: String(r), value: r }));

    return (
      <div ref={ref} className={`${styles.container} ${className || ""}`} style={style} {...rest}>
        <div className={styles.leftLayout}>
          Exibindo {firstRecord}-{lastRecord} de {totalRecords} registros
        </div>
        
        <div className={styles.rightLayout}>
          <div className={styles.rowsPerPage}>
            <span>Itens por página:</span>
            <Dropdown
              value={rows}
              options={rppOptions}
              onChange={(e) => changeRowsPerPage(e.value)}
              className={styles.dropdown}
            />
          </div>
          
          <div className={styles.pageButtons}>
            <IconButton
              icon="chevron_left"
              variant="text"
              severity="secondary"
              aria-label="Página anterior"
              disabled={currentPage === 1 || totalRecords === 0}
              onClick={() => changePage(currentPage - 1)}
              className={styles.navButton}
            />
            
            {visiblePages.map((p, idx) => {
              if (p === '...') {
                return <span key={`ellipsis-${idx}`} className={styles.ellipsis}>...</span>;
              }
              const pageNum = p as number;
              const isActive = pageNum === currentPage;
              return (
                <Button
                  key={pageNum}
                  label={String(pageNum)}
                  variant={isActive ? "filled" : "outlined"}
                  severity={isActive ? "primary" : "neutral"}
                  onClick={() => changePage(pageNum)}
                  className={isActive ? styles.activePage : styles.inactivePage}
                  aria-label={`Página ${pageNum}`}
                />
              );
            })}
            
            <IconButton
              icon="chevron_right"
              variant="text"
              severity="secondary"
              aria-label="Próxima página"
              disabled={currentPage === totalPages || totalRecords === 0}
              onClick={() => changePage(currentPage + 1)}
              className={styles.navButton}
            />
          </div>
        </div>
      </div>
    );
  }
);

Paginator.displayName = "Paginator";

export default Paginator;
